import React from 'react';
import { InterPoint } from '@saviynt/common';
import { Box, Chip, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { SodApplicationGroup } from '../../../../../../../../shared/components';
import {
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_BLD,
  CF_SEC_MED,
} from '../../../../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../../../../shared/hooks';
import { shouldShowEntitlementTrustScore } from '../../../../../../../../shared/utils';
import { type TrackedViolationFunctions } from '../../../../../../data-layer/detailsTypes';

interface ConflictLeadingContentProps {
  level: number;
  isObject?: boolean;
}

/**
 ** @fileoverview ConflictsShared - Shared components for risk conflicts display
 * Contains reusable components for rendering different parts of conflict rows:
 * - ConflictLeadingContent: Icons for different row types
 * - ConflictCenterContent: Main content display for functions, entitlements, and objects
 * - ConflictTrailingContent: Trailing information like counts and status chips
 * - Helper functions for creating conflict rows
 */
function ConflictLeadingContent({
  level,
  isObject,
}: ConflictLeadingContentProps) {
  if (level === 0) {
    return <Icon kind='JobFunction' color={CF_SEC_MED} />;
  }

  if (level >= 2 && isObject) {
    return <Icon kind='Object' color={CF_SEC_MED} />;
  }

  return <Icon kind='Entitlement' color={CF_SEC_MED} />;
}

ConflictLeadingContent.defaultProps = {
  isObject: false,
};

interface ConflictCenterContentProps {
  level: number;
  fnRow?: Record<string, unknown>;
  entRow?: Record<string, unknown>;
  vioRow?: Record<string, unknown>;
  vioObj?: Record<string, unknown>;
  isObject?: boolean;
}

/**
 * ConflictCenterContent
 */
function ConflictCenterContent({
  level,
  fnRow,
  entRow,
  vioRow,
  vioObj,
  isObject,
}: ConflictCenterContentProps) {
  const l = useInvertedLocalize();

  const centerContentClasses = cx(
    'Sod-ViolationsTabDetailsConflict-centerContent',
    `Sod-ViolationsTabDetailsConflict-centerContent--isLevel-${level}`
  );

  if (level === 0 && fnRow) {
    return (
      <Box tag='div' className={centerContentClasses}>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentHeadingWrapper'>
          <Typography kind='body3' color={CF_SEC_MED}>
            {l('Function')}
          </Typography>
          <Typography kind='body1-bold' lineLimit={1} color={CF_NEU_BLD}>
            {String(fnRow.name ?? '')}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentBodyWrapper'>
          <SodApplicationGroup
            label={String(fnRow.endpointName ?? '')}
            firstLogo={fnRow.endpointLogo as string | undefined}
          />
          {fnRow.description ? (
            <>
              <InterPoint color='neutralBold' font='body2' />
              <Typography kind='body2' color={CF_NEU_STL}>
                {l('Description:')}
              </Typography>
              <Typography
                kind='body2'
                color={CF_NEU_STL}
                lineLimit={1}
                isNoWrap>
                {String(fnRow.description as string)}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (level === 1 && (entRow || vioRow)) {
    const rowData = entRow ?? vioRow;

    // SAP Case 1: Single object entry (showObjectFieldLabel: true, object: null)
    if (isObject && vioRow) {
      return (
        <Box tag='div' className={centerContentClasses}>
          <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
            <Typography kind='body3' color={CF_SEC_MED}>
              {l('Object')}
            </Typography>
            <Typography kind='h3' color={CF_NEU_BLD} lineLimit={1}>
              {String(vioRow.objectValue ?? '')}
            </Typography>
          </Box>
          <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Field')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
              {String(vioRow.fieldName ?? '-')}
            </Typography>
          </Box>
          <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('User Value')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
              {String(vioRow.userValue ?? '-')}
            </Typography>
          </Box>
          <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Rule Value')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
              {String(vioRow.ruleValue ?? '-')}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box tag='div' className={centerContentClasses}>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Box className='Sod-ViolationsTabDetailsConflict-centerContentTitleField'>
            <Typography kind='body3' color={CF_SEC_MED}>
              {l('Violating Entitlement')}
            </Typography>
            <InterPoint color='neutralBold' font='body2' />
            <Typography kind='body3' color={CF_SEC_MED} lineLimit={1}>
              {String(rowData?.entitlementTypeName ?? '')}
            </Typography>
          </Box>
          <Typography
            kind='h3'
            lineLimit={1}
            color={CF_NEU_BLD}
            className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapperValue'>
            {String(
              rowData?.entitlementDisplayName ?? rowData?.entitlementValue ?? ''
            )}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('First Used')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_MED} lineLimit={1}>
            {String(rowData?.firstUsed ?? '-')}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Last Used')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_MED} lineLimit={1}>
            {String(rowData?.lastUsed ?? '-')}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Total Used')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_MED} lineLimit={1}>
            {String(rowData?.totalUsed ?? '-')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (level >= 2 && vioObj && isObject) {
    return (
      <Box tag='div' className={centerContentClasses}>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_SEC_MED}>
            {l('Object')}
          </Typography>
          <Typography kind='h3' color={CF_NEU_BLD} lineLimit={1}>
            {String(vioObj.objectValue ?? '')}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Field')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
            {String(vioObj.fieldName ?? '-')}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('User Value')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
            {String(vioObj.userValue ?? '-')}
          </Typography>
        </Box>
        <Box className='Sod-ViolationsTabDetailsConflict-centerContentFieldWrapper'>
          <Typography kind='body3' color={CF_SEC_MED}>
            {l('Rule Value')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
            {String(vioObj.ruleValue ?? '-')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return null;
}

ConflictCenterContent.defaultProps = {
  fnRow: '',
  entRow: '',
  vioRow: '',
  vioObj: '',
  isObject: false,
};

interface ConflictTrailingContentProps {
  level: number;
  fnRow?: Record<string, unknown>;
  entRow?: Record<string, unknown>;
  vioRow?: Record<string, unknown>;
  isObject?: boolean;
}

/**
 * ConflictTrailingContent
 */
function ConflictTrailingContent({
  level,
  fnRow,
  entRow,
  vioRow,
  isObject,
}: ConflictTrailingContentProps) {
  const l = useInvertedLocalize();
  const trailContentWrapperClasses = cx(
    'Sod-ViolationsTabDetailsConflict-trailingContent',
    `Sod-ViolationsTabDetailsConflict-trailingContent--isLevel-${level}`
  );

  if (level === 0 && fnRow) {
    return (
      <Box tag='div' className={trailContentWrapperClasses}>
        <Typography kind='body3' color={CF_NEU_STL}>
          {l('Violating Entitlements')}
        </Typography>
        <Typography kind='body1-bold' color={CF_SEC_MED}>
          {Number(fnRow.violatingEntitlementsCount) || 0}
        </Typography>
      </Box>
    );
  }

  if (isObject) {
    return null;
  }

  if (level === 1 && (entRow || vioRow)) {
    const rowData = entRow ?? vioRow;
    const trustScore = Number(rowData?.entitlementTrustScore ?? 0);

    if (!shouldShowEntitlementTrustScore(trustScore)) {
      return null;
    }

    const isCritical = Boolean(trustScore <= 50);
    const chipClasses = cx(
      isCritical
        ? 'Sod-ViolationsTabDetailsConflict-trailingContent--isCritical'
        : 'Sod-ViolationsTabDetailsConflict-trailingContent--isSuccess'
    );

    return (
      <Box tag='div' className={trailContentWrapperClasses}>
        <Chip
          color={isCritical ? 'critical' : 'success'}
          size='xSmall'
          label={String(trustScore)}
          className={chipClasses}
          leftIcon={<Icon kind={isCritical ? 'Sad' : 'Happy'} />}
        />
      </Box>
    );
  }

  return null;
}

ConflictTrailingContent.defaultProps = {
  fnRow: '',
  entRow: '',
  vioRow: '',
  isObject: false,
};

/**
 * createObjectConflictRow
 */
function createObjectConflictRow({
  obj,
  item,
  uniqueId,
  objIndex,
  handleConflictRow,
}: {
  obj: Record<string, unknown>;
  item: Record<string, unknown>;
  fnRowData: Record<string, unknown>;
  uniqueId: string;
  objIndex: number;
  handleConflictRow: (row: TrackedViolationFunctions) => void;
}) {
  const objectId = `${uniqueId}-object-${objIndex}`;

  return {
    id: objectId,
    data: {
      ...obj,
      ...item,
      tracker: {
        id: objectId,
        level: 3,
        childrenCount: 0,
        type: 'Object',
      },
    },
    isCheckboxDisabled: true,
    leadingComp: <ConflictLeadingContent level={3} isObject />,
    centerContent: <ConflictCenterContent level={3} vioObj={obj} isObject />,
    trailingContent: <ConflictTrailingContent level={3} />,
    handleRowClick() {
      handleConflictRow(item as unknown as TrackedViolationFunctions);
    },
  };
}

/**
 * createEntitlementConflictRow
 */
function createEntitlementConflictRow({
  item,
  uniqueId,
  itemIndex,
  handleConflictRow,
  hasObjects,
  onExpandToggle,
}: {
  item: Record<string, unknown>;
  fnRowData: Record<string, unknown>;
  uniqueId: string;
  itemIndex: number;
  handleConflictRow: (row: TrackedViolationFunctions) => void;
  hasObjects: boolean;
  onExpandToggle: () => void;
}) {
  const childId = `${uniqueId}-child-${itemIndex}`;

  return {
    id: childId,
    data: {
      ...item,
      tracker: {
        id: childId,
        level: 1,
        childrenCount: (() => {
          if (!hasObjects) {
            return 0;
          }

          return Array.isArray(item.object) ? item.object.length : 0;
        })(),
        type: 'Entitlement',
      },
    },
    isCheckboxDisabled: true,
    leadingComp: <ConflictLeadingContent level={1} />,
    centerContent: <ConflictCenterContent level={1} entRow={item} />,
    trailingContent: <ConflictTrailingContent level={1} entRow={item} />,
    handleRowClick() {
      handleConflictRow(item as unknown as TrackedViolationFunctions);
    },
    handleExpandToggler: hasObjects ? onExpandToggle : undefined,
    isExpandable: hasObjects,
  };
}

export {
  ConflictCenterContent,
  ConflictLeadingContent,
  ConflictTrailingContent,
  createEntitlementConflictRow,
  createObjectConflictRow,
};
